import type { Roll, RollGroup, RollSection, Scan, DownloadFormat, DownloadRequest } from '../types/roll';

/**
 * Validates a roll name input.
 * Returns null if valid (non-whitespace, trimmed length ≤ 100),
 * or an error string describing the issue.
 */
export function validateRollName(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return 'Roll name cannot be empty';
  }
  if (trimmed.length > 100) {
    return 'Roll name must be 100 characters or fewer';
  }
  return null;
}

/**
 * Sorts rolls by created_at in ascending order (oldest first).
 * Returns a new array — does not mutate the input.
 */
export function sortRollsByCreatedAt(rolls: Roll[]): Roll[] {
  return [...rolls].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

/**
 * Converts an array of RollGroup objects into RollSection objects,
 * preserving the count and order from the API response.
 */
export function buildRollSections(rolls: RollGroup[]): RollSection[] {
  return rolls.map((roll) => ({
    roll_id: roll.roll_id,
    roll_name: roll.roll_name,
    roll_type: roll.roll_type,
    scans: roll.scans,
  }));
}

/**
 * Flattens all scans across all rolls into a single array,
 * preserving roll order and intra-roll scan order.
 * Used for lightbox navigation across roll boundaries.
 */
export function flattenScansForLightbox(rolls: RollGroup[]): Scan[] {
  return rolls.flatMap((roll) => roll.scans);
}

/**
 * Determines whether the "Select Roll" checkbox should be shown for a roll.
 * Returns true only if the roll contains at least one scan.
 */
export function shouldShowRollCheckbox(roll: RollGroup): boolean {
  return roll.scans.length > 0;
}

/**
 * Builds the download request body with proper deduplication:
 * - roll_ids: rolls where ALL scans are selected (via roll checkbox)
 * - scan_ids: individually selected scans NOT already covered by a selected roll
 */
export function buildDownloadRequest(
  selectedRolls: Set<string>,
  selectedScans: Set<string>,
  rolls: RollGroup[],
  format: DownloadFormat
): DownloadRequest {
  const rollIds = Array.from(selectedRolls);

  // Collect scan_ids covered by selected rolls
  const coveredScanIds = new Set<string>();
  for (const roll of rolls) {
    if (selectedRolls.has(roll.roll_id)) {
      for (const scan of roll.scans) {
        coveredScanIds.add(scan.scan_id);
      }
    }
  }

  // scan_ids are individually selected scans NOT covered by a selected roll
  const scanIds = Array.from(selectedScans).filter((id) => !coveredScanIds.has(id));

  const request: DownloadRequest = { format };
  if (rollIds.length > 0) {
    request.roll_ids = rollIds;
  }
  if (scanIds.length > 0) {
    request.scan_ids = scanIds;
  }
  return request;
}

/**
 * Returns the count of uniquely selected scans.
 */
export function getSelectedCount(selectedScans: Set<string>): number {
  return selectedScans.size;
}

/**
 * Formats a server error for display. For 5xx errors, returns a generic
 * message that never exposes response body content, status codes, or stack traces.
 */
export function formatServerError(status: number, body: string): string {
  void body; // intentionally unused for 5xx — never expose internals
  if (status >= 500 && status <= 599) {
    return 'Something went wrong. Please try again later.';
  }
  return 'The request could not be completed. Please try again.';
}

/**
 * Returns the accessible label for a "Select Roll" checkbox.
 */
export function getRollCheckboxLabel(rollName: string): string {
  return `Select all scans in ${rollName}`;
}
